import { useState, useCallback, useEffect } from "react";
import mammoth from "mammoth";
import { Spin, Button, Tooltip, Space } from "antd";
import {
	DownloadOutlined,
	FullscreenOutlined,
	FullscreenExitOutlined,
	FileWordOutlined,
	ReloadOutlined,
} from "@ant-design/icons";
import styles from "./index.module.scss";

/**
 * Word 文档预览组件
 * 使用 mammoth.js 将 .docx 文件转换为 HTML 进行预览
 * 
 * 支持的格式：docx
 * 注意：不支持 .doc 格式（旧版 Word 格式）
 */
const WordViewer = ({
	url,
	fileName,
	showHeader = true,
	showDownload = true,
	showFullscreen = true,
	height = "600px",
	onError,
	onLoad,
}) => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [htmlContent, setHtmlContent] = useState("");
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [messages, setMessages] = useState([]); // 转换警告信息

	// 加载并转换文档
	const loadDocument = useCallback(async () => {
		if (!url) return;

		setLoading(true);
		setError(null);
		setHtmlContent("");
		setMessages([]);

		try {
			// 获取文件
			const response = await fetch(url);
			if (!response.ok) {
				throw new Error(`文件加载失败: ${response.status}`);
			}

			const arrayBuffer = await response.arrayBuffer();

			// 使用 mammoth 转换为 HTML
			const result = await mammoth.convertToHtml(
				{ arrayBuffer },
				{
					// 样式映射配置
					styleMap: [
						"p[style-name='Heading 1'] => h1:fresh",
						"p[style-name='Heading 2'] => h2:fresh",
						"p[style-name='Heading 3'] => h3:fresh",
						"p[style-name='Heading 4'] => h4:fresh",
						"p[style-name='Heading 5'] => h5:fresh",
						"p[style-name='Heading 6'] => h6:fresh",
					],
					// 图片处理
					convertImage: mammoth.images.imgElement(function (image) {
						return image.read("base64").then(function (imageBuffer) {
							return {
								src: `data:${image.contentType};base64,${imageBuffer}`,
							};
						});
					}),
				}
			);

			setHtmlContent(result.value);
			setMessages(result.messages);
			onLoad?.();

			// 如果有警告信息，输出到控制台
			if (result.messages.length > 0) {
				console.warn("Word 文档转换警告:", result.messages);
			}
		} catch (err) {
			console.error("文档加载失败:", err);
			// setError(err.message || "文档加载失败");
			setError("文档加载失败");
			onError?.(err);
		} finally {
			setLoading(false);
		}
	}, [url, onError, onLoad]);

	// URL 变化时重新加载
	useEffect(() => {
		if (url) {
			loadDocument();
		}
	}, [url, loadDocument]);

	// 下载文件
	const handleDownload = useCallback(() => {
		if (url) {
			const link = document.createElement("a");
			link.href = url;
			link.download = fileName || url.split("/").pop() || "document.docx";
			link.target = "_blank";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		}
	}, [url, fileName]);

	// 切换全屏
	const toggleFullscreen = useCallback(() => {
		setIsFullscreen((prev) => !prev);
	}, []);

	// 重新加载
	const handleReload = useCallback(() => {
		loadDocument();
	}, [loadDocument]);

	// 无 URL 时显示提示
	if (!url) {
		return (
			<div className={styles.wordViewer} style={{ height }}>
				<div className={styles.placeholder}>
					<FileWordOutlined className={styles.placeholderIcon} />
					<div className={styles.placeholderText}>请选择 Word 文档进行预览</div>
				</div>
			</div>
		);
	}

	return (
		<div
			className={`${styles.wordViewer} ${isFullscreen ? styles.fullscreen : ""}`}
			style={{ height: isFullscreen ? "100vh" : height }}
		>
			{/* 工具栏 */}
			{showHeader && (
				<div className={styles.toolbar}>
					<div className={styles.fileInfo}>
						<FileWordOutlined style={{ color: "#2b579a", fontSize: 18 }} />
						<span className={styles.fileName}>
							{fileName || url.split("/").pop() || "Word 文档预览"}
						</span>
					</div>
					<Space size="small">
						<Tooltip title="刷新">
							<Button
								type="text"
								icon={<ReloadOutlined />}
								onClick={handleReload}
								disabled={loading}
							/>
						</Tooltip>
						{showDownload && (
							<Tooltip title="下载">
								<Button
									type="text"
									icon={<DownloadOutlined />}
									onClick={handleDownload}
								/>
							</Tooltip>
						)}
						{showFullscreen && (
							<Tooltip title={isFullscreen ? "退出全屏" : "全屏"}>
								<Button
									type="text"
									icon={
										isFullscreen ? (
											<FullscreenExitOutlined />
										) : (
											<FullscreenOutlined />
										)
									}
									onClick={toggleFullscreen}
								/>
							</Tooltip>
						)}
					</Space>
				</div>
			)}

			{/* 预览区域 */}
			<div className={styles.content}>
				{loading && (
					<div className={styles.loading}>
						<Spin size="large" tip="正在加载文档..." />
					</div>
				)}

				{error ? (
					<div className={styles.error}>
						<FileWordOutlined className={styles.errorIcon} />
						<div className={styles.errorText}>{error}</div>
						<Space>
							<Button onClick={handleReload}>重试</Button>
							{/*showDownload && (
								<Button
									type="primary"
									icon={<DownloadOutlined />}
									onClick={handleDownload}
								>
									下载文件
								</Button>
							)*/}
						</Space>
					</div>
				) : (
					<div className={styles.documentWrapper}>
						<div
							className={styles.documentContent}
							dangerouslySetInnerHTML={{ __html: htmlContent }}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

export default WordViewer;







